'use client';

import { Badge, Button } from '@common/components';
import { assert, cn } from '@core/utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { AuthInfiniteQueryOptions } from '@/api';
import { PointCoin } from '@/app/_components/PointCoin';

import { usePartnersMember } from '../../_hooks/usePartnersMember';
import {
  formatMonthDay,
  formatWon,
  PAYOUT_DAY,
  REWARD_STATUS_BADGE,
  REWARD_STATUS_LABEL,
} from '../../_utils/labels';
import { contextOf } from '../../_utils/queries';
import { PartnersDialogs } from '../PartnersDialogs';
import { PartnersGate } from '../PartnersGate';
import { PartnersShell } from '../PartnersShell';
import { SectionLoadError } from '../SectionLoadError';

interface PartnersRewardsScreenProps {
  isGuest: boolean;
}

interface RewardsSummaryProps {
  amounts: {
    accumulatedAmount: number;
    scheduledAmount: number;
    paidAmount: number;
  } | null;
}

const RewardsSummary = ({ amounts }: RewardsSummaryProps) => (
  <section className="mx-20 flex flex-col gap-12 rounded-medium border border-Gray-200 bg-Gray-white p-20 xl:mx-0">
    <span className="text-Gray-700 typo-caption1">누적 지급액</span>
    <span className="flex items-center gap-8 font-800 text-Gray-900 typo-h5">
      <PointCoin currency="WELLO_POINT" size={32} />
      {amounts ? formatWon(amounts.accumulatedAmount) : '-'}
    </span>
    <hr className="border-0 border-t border-Gray-100" />
    <div className="flex gap-12">
      <div className="flex flex-1 flex-col gap-4">
        <span className="text-Gray-700 typo-caption1">지급 예정</span>
        <span className="font-600 text-B-550 typo-body4">
          {amounts ? formatWon(amounts.scheduledAmount) : '-'}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-4">
        <span className="text-Gray-700 typo-caption1">지급 완료</span>
        <span className="font-600 text-Gray-900 typo-body4">
          {amounts ? formatWon(amounts.paidAmount) : '-'}
        </span>
      </div>
    </div>
    <p className="text-Gray-700 typo-caption1">
      매월 실적은 익월 {PAYOUT_DAY}일 검증 후 웰포인트로 지급돼요.
    </p>
  </section>
);

const RewardsFallback = () => (
  <div className="flex flex-col gap-20 pb-24">
    <RewardsSummary amounts={null} />
  </div>
);

const RewardsContent = () => {
  const rewardsOptions =
    AuthInfiniteQueryOptions['get:/wello/v2/auth/partners/rewards']();
  const { queryFn } = rewardsOptions;
  assert(typeof queryFn === 'function');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery({
      ...rewardsOptions,
      queryFn,
      initialPageParam: 1,
      getNextPageParam: (lastPage, _pages, lastPageParam) =>
        contextOf(lastPage).hasNextYN && typeof lastPageParam === 'number'
          ? lastPageParam + 1
          : undefined,
    });
  const rewards = data.pages.flatMap((page) => contextOf(page).contents);

  return (
    <div className="flex flex-col gap-20 pb-24">
      <RewardsSummary amounts={contextOf(data.pages[0])} />

      {rewards.length === 0 ? (
        <p className="flex flex-col items-center gap-8 px-20 py-80 text-center text-Gray-700 typo-body4">
          <span className="font-700 text-Gray-900 typo-body3">
            아직 보상이 없어요
          </span>
          <span>링크로 들어온 기부가 생기면 여기에 쌓여요</span>
        </p>
      ) : (
        <ul className="flex flex-col px-20 xl:px-0">
          {rewards.map((item) => (
            <li
              key={item.partnersRewardId}
              className="flex items-start justify-between gap-12 border-b border-Gray-200 py-16"
            >
              <div className="flex min-w-0 gap-8">
                <span className="shrink-0 text-Gray-700 typo-body4">
                  {formatMonthDay(item.donatedAt)}
                </span>
                <span className="min-w-0 break-words break-keep font-600 text-Gray-900 typo-body3">
                  {item.contentName}
                </span>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-8">
                <span className="text-Gray-900 typo-body4">
                  {formatWon(item.rewardAmount)}
                </span>
                <Badge
                  className={cn(
                    'xl:px-4 xl:typo-caption2',
                    REWARD_STATUS_BADGE[item.rewardStatus],
                  )}
                >
                  {REWARD_STATUS_LABEL[item.rewardStatus]}
                </Badge>
              </div>
            </li>
          ))}
        </ul>
      )}

      {hasNextPage ? (
        <div className="flex justify-center px-20">
          <Button
            theme="sub-fill"
            dimensions="H48"
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            더보기
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export const PartnersRewardsScreen = ({
  isGuest,
}: PartnersRewardsScreenProps) => {
  const { hasLinkYN, suspendYN } = usePartnersMember(isGuest);

  return (
    <PartnersShell
      mobileTitle="내 정산내역"
      mobileDescription="파트너스 수익의 지급 상태를 확인해요."
    >
      <PartnersGate
        isGuest={isGuest}
        hasLinks={hasLinkYN}
        placeholder={<RewardsFallback />}
      >
        <ErrorBoundary fallback={<SectionLoadError />}>
          <Suspense fallback={<RewardsFallback />}>
            <RewardsContent />
          </Suspense>
        </ErrorBoundary>
      </PartnersGate>
      <PartnersDialogs
        isGuest={isGuest}
        hasLinks={hasLinkYN}
        isSuspended={suspendYN}
      />
    </PartnersShell>
  );
};
